import {
  Form,
  InputNumber,
  Modal,
  Space,
  Table,
  Typography,
  notification,
} from "antd";
import { useContext, useEffect, useState } from "react";

import "../styles.css";

import { Context } from "../../../context";
import { Fetch } from "../../../fetch";
import { convertIdToKey } from "../../../utils";
import {
  putSeriesCoinFields,
  normalizeCoinPrice,
  normalizeValidityDays,
  normalizeBundleSize,
} from "../coinsApi";

const { Column } = Table;

const SeriesCoins = () => {
  const { token, apiUrl } = useContext(Context);

  const [series, setSeries] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [seriesId, setSeriesId] = useState();
  const [seriesTitle, setSeriesTitle] = useState("");
  const [counter, setCounter] = useState(0);

  const [form] = Form.useForm();

  useEffect(() => {
    Fetch(`${apiUrl}/v4/webseries?pageSize=100000`, token, (result) => {
      setSeries(convertIdToKey(result.webseries || []));
    });
  }, [apiUrl, token, counter]);

  const showEditModal = (record) => {
    setSeriesId(record.key);
    setSeriesTitle(record.title);
    setModalVisible(true);
    form.setFieldsValue({
      coinPrice: record.coinPrice ?? null,
      coinBundlePrice: record.coinBundlePrice ?? null,
      coinBundleSize: record.coinBundleSize ?? null,
      coinUnlockValidityDays: record.coinUnlockValidityDays ?? null,
    });
  };

  const closeModal = () => {
    setModalVisible(false);
    setSeriesId(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const fields = await form.validateFields();
      setConfirmLoading(true);

      putSeriesCoinFields(
        apiUrl,
        token,
        seriesId,
        {
          coinPrice: normalizeCoinPrice(fields.coinPrice),
          coinBundlePrice: normalizeCoinPrice(fields.coinBundlePrice),
          coinBundleSize: normalizeBundleSize(fields.coinBundleSize),
          coinUnlockValidityDays: normalizeValidityDays(
            fields.coinUnlockValidityDays
          ),
        },
        (result) => {
          setConfirmLoading(false);
          if (result.success) {
            notification.success({
              message: "Success",
              description: "Series coin settings updated",
            });
            closeModal();
            setCounter((c) => c + 1);
          } else {
            notification.error({
              message: "Error updating series coins",
              description: "Check console for more details",
            });
          }
        },
        () => {
          setConfirmLoading(false);
          notification.error({
            message: "Error updating series coins",
            description: "Check console for more details",
          });
        }
      );
    } catch (err) {
      notification.error({
        message: "Error updating series coins",
        description: "Fields are incorrect/missing",
      });
    }
  };

  return (
    <div className="manage-tasks-wrapper">
      <Table dataSource={series}>
        <Column title="Title" dataIndex="title" key="title" align="center" />
        <Column
          title="Full Series Price (coins)"
          dataIndex="coinPrice"
          key="coinPrice"
          align="center"
          render={(coinPrice) => coinPrice ?? "-"}
        />
        <Column
          title="Bundle Price (coins)"
          dataIndex="coinBundlePrice"
          key="coinBundlePrice"
          align="center"
          render={(coinBundlePrice) => coinBundlePrice ?? "-"}
        />
        <Column
          title="Bundle Episodes"
          dataIndex="coinBundleSize"
          key="coinBundleSize"
          align="center"
          render={(coinBundleSize) => coinBundleSize ?? "-"}
        />
        <Column
          title="Unlock Validity (days)"
          dataIndex="coinUnlockValidityDays"
          key="coinUnlockValidityDays"
          align="center"
          render={(days) => days ?? "-"}
        />
        <Column
          title="Actions"
          key="actions"
          align="center"
          render={(text, record) => (
            <Space size="middle">
              <Typography.Link onClick={() => showEditModal(record)}>
                Edit
              </Typography.Link>
            </Space>
          )}
        />
      </Table>
      <Modal
        title={`Series Coins — ${seriesTitle}`}
        visible={modalVisible}
        onOk={handleSubmit}
        okText="Submit"
        onCancel={closeModal}
        confirmLoading={confirmLoading}
        destroyOnClose={true}
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="coinPrice"
            label="Full series price (coins)"
            extra="Blank = global default (Unlock Config) · 0 = disabled for this series"
            rules={[
              {
                type: "integer",
                min: 0,
                message: "Full series price must be an integer >= 0",
              },
            ]}
          >
            <InputNumber
              min={0}
              precision={0}
              placeholder="Enter full series price"
            />
          </Form.Item>
          <Form.Item
            name="coinBundlePrice"
            label="Bundle price (coins)"
            extra="Blank = global default (Unlock Config) · 0 = disabled for this series"
            rules={[
              {
                type: "integer",
                min: 0,
                message: "Bundle price must be an integer >= 0",
              },
            ]}
          >
            <InputNumber
              min={0}
              precision={0}
              placeholder="Enter bundle price"
            />
          </Form.Item>
          <Form.Item
            name="coinBundleSize"
            label="Bundle episodes"
            extra="Episodes per bundle; blank = use global default (Unlock Config)"
            rules={[
              {
                type: "integer",
                min: 1,
                message: "Bundle episodes must be an integer >= 1",
              },
            ]}
          >
            <InputNumber
              min={1}
              precision={0}
              placeholder="Enter episodes per bundle"
            />
          </Form.Item>
          <Form.Item
            name="coinUnlockValidityDays"
            label="Coin unlock validity (days)"
            extra="How long a coin unlock lasts (default 30)"
            rules={[
              {
                type: "integer",
                min: 1,
                message: "Coin unlock validity must be an integer >= 1",
              },
            ]}
          >
            <InputNumber
              min={1}
              precision={0}
              placeholder="Enter validity in days (default 30)"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SeriesCoins;
